import { useState, useEffect, useCallback } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Represents a country
 */
export interface Country {
  id: string
  name: string
  name_full: string
  iso_3166_2: string
}

/**
 * Represents a province/state
 */
export interface Province {
  id: string
  name: string
  iso_3166_2: string
}

/**
 * Represents an address
 */
export interface Address {
  id: string
  name: string | null
  name_full: string | null
  street_1: string
  street_2: string | null
  city: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  country?: Country
  province?: Province
}

/**
 * Represents a facility (surface) within a venue
 */
export interface Facility {
  id: string
  name: string
}

/**
 * Represents a venue (location)
 */
export interface Venue {
  id: string
  name: string
  name_full: string
  email: string | null
  website: string | null
  phone: string | null
  timezone: string
  abbreviation: string | null
  has_disabled_access: boolean
  has_bar: boolean
  parking: string
  is_primary: boolean
  address: Address
  facilities: Facility[]
}

/**
 * Hook result interface
 */
interface UseSchedulerLocationsResult {
  /** List of venues for the competition */
  venues: Venue[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Function to fetch locations */
  fetchLocations: (scheduleId: string) => Promise<void>
  /** Function to refetch current locations */
  refetch: () => Promise<void>
}

/**
 * Custom hook to fetch and manage locations/venues for the scheduler wizard
 * @param competitionId - Optional competition/schedule ID to fetch locations for
 * @returns Object containing venues data, loading state, error state, and fetch functions
 */
export const useSchedulerLocations = (
  competitionId?: string
): UseSchedulerLocationsResult => {
  const [venues, setVenues] = useState<Venue[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetches venues data from the API for a specific schedule
   * @param scheduleId - The schedule/competition ID to fetch venues for
   */
  const fetchLocations = useCallback(async (scheduleId: string) => {
    if (!scheduleId) {
      setVenues([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await req('/venues', {
        query: { schedule_id: scheduleId },
      })
      setVenues(data || [])
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching locations')
      )
      setVenues([])
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Refetches venues for the current competition
   */
  const refetch = useCallback(async () => {
    if (competitionId) {
      await fetchLocations(competitionId)
    }
  }, [competitionId, fetchLocations])

  // Fetch locations when competitionId changes
  useEffect(() => {
    if (competitionId) {
      fetchLocations(competitionId)
    } else {
      setVenues([])
    }
  }, [competitionId, fetchLocations])

  return {
    venues,
    loading,
    error,
    fetchLocations,
    refetch,
  }
}

export default useSchedulerLocations
